// core/SceneManager.ts
import * as THREE from 'three';
import { releaseGeometry, releaseMaterial } from '../orbs/OrbResources';
import { releaseMaskedTexture } from '../services/creation/ImageLoading';
const DEFAULT_SCENE = 'system';
/**
 * Coordinates creation, selection, and cleanup of registered Three.js scenes.
 * SceneManager intentionally avoids world/orb-specific rules; it exposes events
 * and helpers so other subsystems can layer policies (physics, portals, etc.)
 * while SceneManager stays responsible for resource tracking and object routing.
 */
class SceneManager {
    constructor() {
        this.scenes = new Map();
        this.currentSceneName = DEFAULT_SCENE;
        // --- lightweight typed event emitter ---
        this.listeners = new Map();
        this.createScene(DEFAULT_SCENE);
    }
    static getInstance() {
        if (!SceneManager.instance)
            SceneManager.instance = new SceneManager();
        return SceneManager.instance;
    }
    // ----------------------------
    // Events
    // ----------------------------
    on(eventName, handler) {
        if (!this.listeners.has(eventName))
            this.listeners.set(eventName, new Set());
        this.listeners.get(eventName).add(handler);
        // return unsubscribe
        return () => this.off(eventName, handler);
    }
    off(eventName, handler) {
        var _a;
        (_a = this.listeners.get(eventName)) === null || _a === void 0 ? void 0 : _a.delete(handler);
    }
    emit(eventName, payload) {
        const set = this.listeners.get(eventName);
        if (!set || set.size === 0)
            return;
        for (const handler of set) {
            try {
                handler(payload);
            }
            catch (err) {
                console.warn(`[SceneManager] listener error on "${String(eventName)}":`, err);
            }
        }
    }
    // ----------------------------
    // Scene lifecycle
    // ----------------------------
    //TODO  handle fog stuff here
    createScene(name = DEFAULT_SCENE) {
        const resolvedSceneName = (name || DEFAULT_SCENE).trim();
        const existingBucket = this.scenes.get(resolvedSceneName);
        if (existingBucket)
            return existingBucket.scene;
        const newBucket = {
            name: resolvedSceneName,
            scene: new THREE.Scene(),
            objects: new Map(),
        };
        this.scenes.set(resolvedSceneName, newBucket);
        this.emit('sceneCreated', { sceneName: resolvedSceneName, scene: newBucket.scene });
        return newBucket.scene;
    }
    /** Back-compat: no-arg returns the current (default) scene. */
    getScene(name) {
        const resolvedSceneName = name !== null && name !== void 0 ? name : this.currentSceneName;
        if (!this.scenes.has(resolvedSceneName))
            this.createScene(resolvedSceneName);
        return this.scenes.get(resolvedSceneName).scene;
    }
    setCurrentScene(name) {
        const resolvedSceneName = (name || DEFAULT_SCENE).trim();
        if (!this.scenes.has(resolvedSceneName))
            this.createScene(resolvedSceneName);
        this.currentSceneName = resolvedSceneName;
        this.emit('currentChanged', { sceneName: resolvedSceneName });
    }
    getCurrentSceneName() {
        return this.currentSceneName;
    }
    hasScene(name) {
        return this.scenes.has(name);
    }
    listScenes() {
        return Array.from(this.scenes.keys());
    }
    /** Remove all objects from the scene (optionally disposing) but keep the scene bucket alive. */
    clearScene(name, options = {}) {
        const sceneBucket = this.scenes.get(name);
        if (!sceneBucket)
            return;
        const dispose = !!options.dispose;
        let removedCount = 0;
        // Emit objectRemoved for each, then actually remove + (optional) dispose
        for (const object3D of Array.from(sceneBucket.objects.values())) {
            this.emit('objectRemoved', { sceneName: sceneBucket.name, object: object3D, disposed: dispose });
            sceneBucket.scene.remove(object3D);
            if (dispose)
                this.disposeObject(object3D);
            removedCount++;
        }
        sceneBucket.objects.clear();
        this.emit('sceneCleared', { sceneName: sceneBucket.name, disposed: dispose, removedCount });
    }
    /** Delete the scene entirely. Default scene is protected (it will be cleared instead). */
    deleteScene(name, options = {}) {
        var _a;
        if (name === DEFAULT_SCENE) {
            this.clearScene(DEFAULT_SCENE, options);
            return;
        }
        const sceneBucket = this.scenes.get(name);
        if (!sceneBucket)
            return;
        const dispose = !!options.dispose;
        let removedCount = 0;
        for (const object3D of Array.from(sceneBucket.objects.values())) {
            this.emit('objectRemoved', { sceneName: sceneBucket.name, object: object3D, disposed: dispose });
            sceneBucket.scene.remove(object3D);
            if (dispose)
                this.disposeObject(object3D);
            removedCount++;
        }
        this.scenes.delete(name);
        this.emit('sceneDeleted', { sceneName: name, disposed: dispose, removedCount });
        if (this.currentSceneName === name) {
            this.currentSceneName = this.scenes.has(DEFAULT_SCENE)
                ? DEFAULT_SCENE
                : ((_a = this.listScenes()[0]) !== null && _a !== void 0 ? _a : DEFAULT_SCENE);
            if (!this.scenes.has(this.currentSceneName)) {
                this.createScene(this.currentSceneName);
            }
            this.emit('currentChanged', { sceneName: this.currentSceneName });
        }
    }
    // ----------------------------
    // Object management
    // ----------------------------
    /** Add an object to a scene (defaults to current). Detaches from any other managed scenes first. */
    addObject(object, sceneName) {
        const targetBucket = this.requireBucket(sceneName !== null && sceneName !== void 0 ? sceneName : this.currentSceneName);
        for (const sceneBucket of this.scenes.values()) {
            if (sceneBucket.objects.has(object.uuid)) {
                sceneBucket.scene.remove(object);
                sceneBucket.objects.delete(object.uuid);
                // Emit removed-from-previous (no disposal)
                this.emit('objectRemoved', { sceneName: sceneBucket.name, object, disposed: false });
            }
        }
        targetBucket.scene.add(object);
        targetBucket.objects.set(object.uuid, object);
        this.emit('objectAdded', { sceneName: targetBucket.name, object });
    }
    /**
     * Remove an object. If `sceneName` is provided, remove only from that scene;
     * otherwise remove from any scene that contains it. Optionally dispose.
     */
    removeObject(object, sceneName, options = {}) {
        const dispose = !!options.dispose;
        if (sceneName) {
            const sceneBucket = this.scenes.get(sceneName);
            if (!sceneBucket)
                return;
            if (sceneBucket.objects.has(object.uuid)) {
                sceneBucket.scene.remove(object);
                sceneBucket.objects.delete(object.uuid);
                this.emit('objectRemoved', { sceneName, object, disposed: dispose });
                if (dispose)
                    this.disposeObject(object);
            }
            return;
        }
        for (const sceneBucket of this.scenes.values()) {
            if (sceneBucket.objects.has(object.uuid)) {
                sceneBucket.scene.remove(object);
                sceneBucket.objects.delete(object.uuid);
                this.emit('objectRemoved', { sceneName: sceneBucket.name, object, disposed: dispose });
                if (dispose)
                    this.disposeObject(object);
            }
        }
    }
    /** Same as removeObject but by uuid; scoped or global depending on sceneName. */
    removeObjectById(uuid, sceneName, options = {}) {
        var _a, _b;
        // delegate to removeObject to ensure events fire exactly once
        const target = sceneName
            ? (_a = this.scenes.get(sceneName)) === null || _a === void 0 ? void 0 : _a.objects.get(uuid)
            : (_b = this.findObject(uuid)) === null || _b === void 0 ? void 0 : _b.object;
        if (target)
            this.removeObject(target, sceneName, options);
    }
    /** All objects for a scene (defaults to current). */
    getObjects(sceneName) {
        const resolvedSceneName = sceneName !== null && sceneName !== void 0 ? sceneName : this.currentSceneName;
        const sceneBucket = this.scenes.get(resolvedSceneName);
        if (!sceneBucket)
            return [];
        return Array.from(sceneBucket.objects.values());
    }
    /** Find an object by uuid and return also which scene holds it. */
    findObject(uuid) {
        for (const sceneBucket of this.scenes.values()) {
            const object3D = sceneBucket.objects.get(uuid);
            if (object3D)
                return { object: object3D, sceneName: sceneBucket.name };
        }
        return null;
    }
    /** Resolve the registered name for a scene instance, if it belongs to the manager. */
    getSceneNameByInstance(scene) {
        for (const name of this.scenes.keys()) {
            if (this.scenes.get(name).scene === scene)
                return name;
        }
        return undefined;
    }
    /** True if the given Object3D is currently registered in any managed scene. */
    containsObject(object) {
        for (const bucket of this.scenes.values()) {
            if (bucket.objects.has(object.uuid))
                return true;
        }
        return false;
    }
    // ----------------------------
    // Internals
    // ----------------------------
    requireBucket(name) {
        const resolvedSceneName = (name || DEFAULT_SCENE).trim();
        if (!this.scenes.has(resolvedSceneName))
            this.createScene(resolvedSceneName);
        return this.scenes.get(resolvedSceneName);
    }
    /**
     * Safely dispose geometry, materials, and textures on the material(s).
     * Note: This only frees GPU resources. It does **not** notify OrbManager directly;
     * events above are emitted *before* disposal to allow listeners to react.
     */
    disposeObject(object) {
        object.traverse((child) => {
            const userData = child.userData;
            const maskedTextureKey = userData === null || userData === void 0 ? void 0 : userData.__maskedTextureKey;
            const maskedTextureRelease = userData === null || userData === void 0 ? void 0 : userData.__maskedTextureRelease;
            const maskedTexture = userData === null || userData === void 0 ? void 0 : userData.__maskedTexture;
            const resources = (child.userData && child.userData.__orbResources);
            if (resources === null || resources === void 0 ? void 0 : resources.geometryKey) {
                releaseGeometry(resources.geometryKey);
                resources.geometryKey = undefined;
            }
            else if (child.geometry && typeof child.geometry.dispose === 'function') {
                child.geometry.dispose();
            }
            const material = child.material;
            if (!material)
                return;
            if (resources === null || resources === void 0 ? void 0 : resources.materialKey) {
                releaseMaterial(resources.materialKey);
                resources.materialKey = undefined;
                return;
            }
            const disposeMaterial = (mat) => {
                for (const key of Object.keys(mat)) {
                    const maybeTexture = mat[key];
                    if (maskedTexture && maybeTexture === maskedTexture) {
                        continue;
                    }
                    if (maybeTexture && maybeTexture.isTexture && typeof maybeTexture.dispose === 'function') {
                        maybeTexture.dispose();
                    }
                }
                if (typeof mat.dispose === 'function')
                    mat.dispose();
            };
            if (Array.isArray(material)) {
                for (const m of material)
                    disposeMaterial(m);
            }
            else {
                disposeMaterial(material);
            }
            if (maskedTextureRelease) {
                try {
                    maskedTextureRelease();
                }
                catch (_a) { }
            }
            else if (maskedTextureKey) {
                releaseMaskedTexture(maskedTextureKey);
            }
            if (userData) {
                delete userData.__maskedTextureKey;
                delete userData.__maskedTextureRelease;
                delete userData.__maskedTexture;
            }
        });
    }
}
export default SceneManager;
